import React, { useState } from "react";
import { Megaphone, Building2, BadgeCheck, CalendarDays, Users, Sparkles, ArrowRight, Check } from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "FEATURED_PROPERTY", icon: Building2, title: "Featured Property",
    desc: "Increase the visibility of selected property listings within PropertyConnect.", cta: "Express Interest" },
  { key: "FEATURED_PROFESSIONAL", icon: BadgeCheck, title: "Featured Professional",
    desc: "Raise the profile of a verified real-estate professional across the network.", cta: "Express Interest" },
  { key: "FEATURED_EVENT", icon: CalendarDays, title: "Featured Open House / Investors Night",
    desc: "Promote open houses, investors' nights and project presentations to the PropertyConnect audience.", cta: "Express Interest" },
  { key: "RECRUITMENT", icon: Users, title: "Recruitment Advertising",
    desc: "For sales managers, sales directors, brokerages and developers reaching real-estate professionals.", cta: "Express Interest" },
  { key: "SPONSORED_PLACEMENT", icon: Sparkles, title: "Sponsored Placements", soon: true,
    desc: "Reserved for future commercial placements across PropertyConnect surfaces.", cta: "Contact PropertyConnect" },
];

export default function Advertise() {
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState({});

  const express = async (cat) => {
    if (busy || done[cat.key]) return;
    setBusy(cat.key);
    try {
      await api.post("/advertising-interest", { category: cat.key });
      // Server records the advertise_interest analytics event authoritatively; no client emit needed.
      setDone((d) => ({ ...d, [cat.key]: true }));
      toast.success("Thanks — your interest has been recorded. We'll let you know when this advertising option becomes available.");
    } catch (e) {
      toast.error("We couldn't record that just now. Please try again shortly.");
    } finally {
      setBusy("");
    }
  };

  return (
    <PageShell testid="advertise-page">
      <section className="rounded-2xl bg-gradient-to-br from-[#0F2C59] to-[#143163] text-white px-6 sm:px-10 py-10 sm:py-14 border border-[#C79A3B]/20">
        <p className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C79A3B]"><Megaphone className="h-4 w-4" /> Advertise</p>
        <h1 className="font-display text-3xl sm:text-5xl font-semibold tracking-tight mt-3 max-w-3xl">Advertise with PropertyConnect</h1>
        <p className="text-white/80 mt-4 max-w-2xl text-sm sm:text-base leading-relaxed">
          Reach professionals and property seekers already engaged in real-estate discovery, matching
          and collaboration. Tell us what you'd like to promote and we'll let you know as these
          advertising options become available.
        </p>
      </section>

      <div className="flex flex-wrap justify-center gap-5 mt-8">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const finished = !!done[c.key];
          return (
            <div key={c.key} data-testid={`advertise-card-${c.key}`} className="flex flex-col bg-card border border-border rounded-2xl p-6 hover:shadow-md transition-shadow w-full sm:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-0.834rem)]">
              <div className="flex items-center justify-between">
                <div className="h-11 w-11 rounded-xl bg-[#0F2C59]/6 text-[#0F2C59] flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                {c.soon && <span className="text-[10px] font-semibold uppercase tracking-wider text-[#C79A3B] bg-[#C79A3B]/10 rounded-full px-2.5 py-1">Coming Soon</span>}
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-900 mt-4">{c.title}</h3>
              <p className="text-sm text-slate-500 mt-1.5 leading-relaxed flex-1">{c.desc}</p>
              <Button
                data-testid={`advertise-cta-${c.key}`}
                onClick={() => express(c)}
                disabled={busy === c.key || finished}
                className={`mt-5 rounded-xl font-semibold ${finished ? "bg-emerald-600 hover:bg-emerald-600 text-white" : "bg-accent text-accent-foreground hover:bg-accent/90"}`}
              >
                {finished ? (<><Check className="h-4 w-4 mr-1.5" /> Interest recorded</>) : busy === c.key ? "Recording…" : (<>{c.cta} <ArrowRight className="h-4 w-4 ml-1.5" /></>)}
              </Button>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-slate-400 mt-8 max-w-2xl">
        PropertyConnect advertising is in early access. Expressing interest helps us prioritise which options to
        launch first — it does not create a booking, commitment or charge.
      </p>
    </PageShell>
  );
}
